import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDateLocale } from '../../../hooks/shared';
import { parseUrlParams } from '../../../lib/navigationUtils';
import {
  useEventInstanceStore,
  useScheduleStore,
  useUserStore,
} from '../../../stores';
import { DaySchedule } from '../types/calendarTypes';
import { buildSchedulesData } from '../utils/scheduleUtils';

/**
 * Hook for managing Home component data and state
 *
 * @returns Object containing:
 * - URL parameters (calendarCurrentDate, isTrainingMode, etc.)
 * - Data (schedules)
 * - Stores (eventInstanceStore, scheduleStore, userStore)
 * - States (isLoading, hasError, isWorkspaceLoading)
 * - Utils (format)
 */
export const useHomeData = () => {
  const params = useParams();
  const { format } = useDateLocale();
  const eventInstanceStore = useEventInstanceStore();
  const scheduleStore = useScheduleStore();
  const userStore = useUserStore();

  // Parse URL parameters
  const urlParams = useMemo(() => parseUrlParams(params), [params]);

  // Load event instances when schedule is available
  useEffect(() => {
    if (scheduleStore.hasCurrentSchedule && !eventInstanceStore.hasData) {
      eventInstanceStore.loadEventInstancesForCurrentSchedule();
    }
  }, [
    scheduleStore.hasCurrentSchedule,
    eventInstanceStore.hasData,
    eventInstanceStore,
  ]);

  // Convert EventInstance data to DaySchedule format for calendar
  const schedules: DaySchedule[] = useMemo(() => {
    return buildSchedulesData(
      eventInstanceStore,
      scheduleStore,
      userStore,
      urlParams.calendarView,
      urlParams.calendarCurrentDate,
      format
    );
  }, [
    eventInstanceStore.instancesByDate,
    scheduleStore.templates,
    urlParams.calendarView,
    urlParams.calendarCurrentDate,
    userStore.userId,
    userStore.users,
    userStore.user?.roles,
    format,
  ]);

  // Loading states
  const isLoading =
    scheduleStore.isLoading ||
    eventInstanceStore.isLoading ||
    userStore.isLoading ||
    !userStore.userId;

  const hasError = scheduleStore.isError || eventInstanceStore.isError;

  const isWorkspaceLoading = !urlParams.workspaceSlug;

  return {
    // URL parameters
    ...urlParams,

    // Data
    schedules,

    // Stores
    eventInstanceStore,
    scheduleStore,
    userStore,

    // States
    isLoading,
    hasError,
    isWorkspaceLoading,

    // Utils
    format,
  };
};
